<template>
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-4">
        <!-- Connectors Selection - Left side (2/3 width) -->
        <div class="lg:col-span-2">
            <div class="space-y-6">
                <!-- Search Section -->
                <SearchInput
                    :placeholder="`Search ${props.type?.toLowerCase()}s...`"
                    custom-class="max-w-md"
                    @search="connectorSearchQuery = $event" />

                <!-- Connectors Grid -->
                <ScrollArea class="h-75-dynamic">
                    <div class="space-y-4 pr-4">
                        <!-- Skeleton loading state -->
                        <template v-if="loading">
                            <div v-for="n in 8" :key="`skeleton-${n}`" class="relative">
                                <Card class="transition-all">
                                    <CardContent class="flex items-center gap-3">
                                        <SkeletonLoader type="circle" class="h-10 w-10" :loading="true" />
                                        <div class="flex-1">
                                            <SkeletonLoader class="h-5 w-48" :loading="true" />
                                        </div>
                                        <SkeletonLoader class="h-4 w-4" :loading="true" />
                                    </CardContent>
                                </Card>
                            </div>
                        </template>

                        <!-- Actual connector data -->
                        <template v-else>
                            <div v-for="(group, groupName) in filteredConnectorGroups" :key="groupName" class="relative">
                                <!-- Single connector - direct selection -->
                                <Card
                                    v-if="group.length === 1"
                                    class="group cursor-pointer transition-colors hover:bg-accent"
                                    @click="selectItem(group[0])">
                                    <CardContent class="flex items-center gap-3">
                                        <Avatar class="h-10 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                            <AvatarImage
                                                :src="getConnectorIcon(groupName) || `/icons/${groupName}.svg`"
                                                :alt="groupName"
                                                class="object-contain p-1" />
                                            <AvatarFallback class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                {{ groupName.charAt(0).toUpperCase() }}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div class="flex-1">
                                            <h3 class="font-semibold group-hover:text-primary transition-colors">
                                                {{ group[0].display_name }}
                                            </h3>
                                        </div>
                                        <ChevronRight class="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    </CardContent>
                                </Card>

                                <!-- Multiple connectors - expandable with inline children -->
                                <Collapsible
                                    v-else
                                    v-model:open="expandedGroups[groupName]"
                                    @update:open="
                                        (open) => {
                                            if (open) handleScrollToExpanded(groupName);
                                        }
                                    ">
                                    <CollapsibleTrigger asChild>
                                        <Card :ref="(el) => setCardRef(groupName, el)" class="group cursor-pointer transition-colors hover:bg-accent">
                                            <CardContent class="flex items-center gap-3">
                                                <Avatar
                                                    class="h-10 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                    <AvatarImage
                                                        :src="getConnectorIcon(groupName) || `/icons/${groupName}.svg`"
                                                        :alt="groupName"
                                                        class="object-contain p-1" />
                                                    <AvatarFallback
                                                        class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                        {{ groupName.charAt(0).toUpperCase() }}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div class="flex-1">
                                                    <h3 class="font-semibold group-hover:text-primary transition-colors">
                                                        {{ groupName }}
                                                    </h3>
                                                </div>
                                                <Badge variant="secondary" class="mr-2">
                                                    {{ group.length }}
                                                </Badge>
                                                <ChevronsUpDown
                                                    class="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                                            </CardContent>
                                        </Card>
                                    </CollapsibleTrigger>

                                    <!-- Child cards - expandable content -->
                                    <CollapsibleContent class="ml-6 mt-3 space-y-2">
                                        <Card
                                            v-for="(child, index) in group"
                                            :key="index"
                                            class="group cursor-pointer transition-colors hover:bg-accent/50"
                                            @click="selectItem(child)">
                                            <CardContent class="flex items-center gap-3">
                                                <Avatar
                                                    class="h-8 w-8 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                    <AvatarImage
                                                        :src="getConnectorIcon(child.connector) || `/icons/${child.connector}.svg`"
                                                        :alt="child.connector"
                                                        class="object-contain p-0.5" />
                                                    <AvatarFallback
                                                        class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                        {{ child.connector.charAt(0).toUpperCase() }}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div class="flex-1">
                                                    <p class="font-semibold text-sm group-hover:text-primary transition-colors">
                                                        {{ child.display_name }}
                                                    </p>
                                                </div>
                                                <ChevronRight class="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors" />
                                            </CardContent>
                                        </Card>
                                    </CollapsibleContent>
                                </Collapsible>
                            </div>

                            <!-- Empty State -->
                            <Card v-if="!Object.keys(filteredConnectorGroups).length" class="border-dashed">
                                <CardContent class="flex flex-col items-center justify-center p-8 text-center">
                                    <Search class="h-12 w-12 text-muted-foreground/50 mb-4" />
                                    <CardTitle class="mb-2">No {{ props.type?.toLowerCase() }}s found</CardTitle>
                                    <p class="text-sm text-muted-foreground mb-4">Try adjusting your search or browse all available connectors</p>
                                    <Button variant="outline" size="sm" @click="connectorSearchQuery = ''"> Clear search </Button>
                                </CardContent>
                            </Card>
                        </template>
                    </div>
                    <ScrollBar orientation="vertical" />
                </ScrollArea>
            </div>
        </div>

        <!-- Help Section - Right side (1/3 width) -->
        <div class="space-y-6 mt-16">
            <!-- Invite Colleague Card -->
            <Card>
                <CardHeader>
                    <CardTitle class="flex items-center gap-2 text-base">
                        <UserPlus class="h-5 w-5" />
                        Need help?
                    </CardTitle>
                </CardHeader>
                <CardContent class="space-y-4">
                    <p class="text-sm text-muted-foreground">
                        Not sure which {{ props.type?.toLowerCase() }} to connect? Invite a colleague who does.
                    </p>
                    <Button @click="openInviteModal = true" class="w-full">
                        <UserPlus class="mr-2 h-4 w-4" />
                        Invite a colleague
                    </Button>
                </CardContent>
            </Card>

            <!-- Resources Card -->
            <Card>
                <CardHeader>
                    <CardTitle class="flex items-center gap-2 text-base">
                        <BookOpen class="h-5 w-5" />
                        Helpful resources
                    </CardTitle>
                </CardHeader>
                <CardContent class="space-y-4">
                    <p class="text-sm text-muted-foreground">
                        <span v-if="props.type?.toLowerCase() === 'source'">
                            A Source is where your data originates. Connect to databases, APIs, and other data sources.
                        </span>
                        <span v-else-if="props.type?.toLowerCase() === 'destination'">
                            A Destination is where your data is sent. Connect to warehouses, databases, and analytics platforms.
                        </span>
                    </p>
                    <Separator />
                    <div class="space-y-3">
                        <a
                            href="https://docs.streamkap.com/docs/getting-started"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex items-center gap-2 text-sm text-primary hover:underline">
                            Get started with Streamkap
                            <ExternalLink class="h-3 w-3" />
                        </a>
                        <a
                            href="https://docs.streamkap.com/docs/connection-options"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex items-center gap-2 text-sm text-primary hover:underline">
                            Connection options
                            <ExternalLink class="h-3 w-3" />
                        </a>
                        <a
                            href="https://docs.streamkap.com/docs/streamkap-ip-addresses"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex items-center gap-2 text-sm text-primary hover:underline">
                            Streamkap IP addresses
                            <ExternalLink class="h-3 w-3" />
                        </a>
                    </div>
                </CardContent>
            </Card>
        </div>

        <!-- Invitation Dialog -->
        <ModalComponent
            :is-open="openInviteModal"
            :show-footer-buttons="true"
            title="Invite a colleague"
            size="md"
            @update:isOpen="openInviteModal = $event">
            <div class="space-y-4">
                <p class="text-sm text-muted-foreground">
                    Send an invitation to a colleague to help you set up this {{ props.type?.toLowerCase() }}.
                </p>
                <div class="space-y-2">
                    <Label for="colleagueEmail">Colleague's Email</Label>
                    <Input id="colleagueEmail" v-model="colleagueEmail" type="email" placeholder="email@example.com" />
                </div>
                <div class="space-y-2">
                    <Label for="invitationMessage">Invitation Message</Label>
                    <Textarea id="invitationMessage" v-model="invitationMessage" rows="8" placeholder="Your message..." />
                </div>
            </div>

            <template #footer>
                <Button variant="outline" @click="openInviteModal = false"> Cancel </Button>
                <Button :disabled="!canSendInvitation" @click="sendInvitation">
                    <div
                        v-if="props.invitationLoading"
                        class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-r-transparent"></div>
                    Send Invitation
                </Button>
            </template>
        </ModalComponent>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useFrontegg } from '@frontegg/vue';
import { getConnectorIcon } from '@/utils/constants';
import { isValidEmail } from '@/utils/validators';
import { ExternalLink, BookOpen, UserPlus, Search, ChevronRight, ChevronsUpDown } from 'lucide-vue-next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import ModalComponent from '@/components/global/modals/Modal.vue';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import SearchInput from '@/components/global/SearchInput.vue';

const frontegg = useFrontegg();

const props = defineProps({
    connectorGroups: {
        type: Object,
        required: true
    },
    type: {
        type: String,
        required: true
    },
    invitationLoading: {
        type: Boolean,
        required: true,
        default: false
    },
    closeInviteModal: {
        type: Boolean,
        default: false
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['select-connector', 'send-invitation']);
const invitationMessage = ref(`Hello!

I have invited you to setup a ${props.type} connector in Streamkap, a real-time streaming platform.
You should have received another invite to sign up (if you don't already have an account).
If you need any clarifications, please get in touch with me.

Thank you!`);

const connectorSearchQuery = ref('');
const colleagueEmail = ref('');
const openInviteModal = ref(false);
const expandedGroups = ref({});
const cardRefs = ref({});

const filteredConnectorGroups = computed(() => {
    if (connectorSearchQuery.value) {
        return Object.keys(props.connectorGroups).reduce((acc, groupName) => {
            const filteredGroup = props.connectorGroups[groupName].filter((x) =>
                x.display_name.toLowerCase().includes(connectorSearchQuery.value.toLowerCase())
            );
            if (filteredGroup.length) {
                acc[groupName] = filteredGroup;
            }
            return acc;
        }, {});
    }

    return props.connectorGroups;
});

watch(
    () => props.closeInviteModal,
    (closeInviteModal) => {
        if (closeInviteModal) {
            colleagueEmail.value = '';
            invitationMessage.value = '';

            openInviteModal.value = false;
        }
    }
);

const canSendInvitation = computed(() => {
    return isValidEmail(colleagueEmail.value) && !!invitationMessage.value;
});

const setCardRef = (groupName, el) => {
    if (el) {
        // Get the actual DOM element from the Vue component instance
        cardRefs.value[groupName] = el.$el || el;
    }
};

// Since we're using Collapsible, we need to handle scroll differently
// Let's add a method to handle scrolling that we can call when needed
const handleScrollToExpanded = (groupName) => {
    setTimeout(() => {
        const parentCardEl = cardRefs.value[groupName];
        if (parentCardEl) {
            // Try multiple ways to find the expanded content
            const collapsibleWrapper = parentCardEl.closest('[data-radix-collapsible-root]') || parentCardEl.parentElement;
            const collapsibleContent =
                collapsibleWrapper?.querySelector('[data-radix-collapsible-content]') || collapsibleWrapper?.querySelector('.ml-6.mt-3.space-y-2');

            // Find the last actual child card using multiple strategies
            let lastChildCard = null;

            if (collapsibleContent) {
                // Try to find the last Card component within the collapsible content
                const allCards = collapsibleContent.querySelectorAll('[class*="cursor-pointer"]');
                lastChildCard = allCards[allCards.length - 1];

                // Fallback: use the last child element
                if (!lastChildCard && collapsibleContent.children.length > 0) {
                    lastChildCard = collapsibleContent.children[collapsibleContent.children.length - 1];
                }
            }

            let targetElement = lastChildCard || collapsibleContent || parentCardEl;

            if (targetElement && typeof targetElement.scrollIntoView === 'function') {
                // Try scrolling to the bottom of the element
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest'
                });
            } else {
            }
        } else {
        }
    }, 400); // Wait longer for Collapsible animation to complete
};

// Watch for changes in expandedGroups to trigger scrolling
watch(
    expandedGroups,
    (newValue, oldValue) => {
        Object.keys(newValue).forEach((groupName) => {
            if (newValue[groupName] && !oldValue?.[groupName]) {
                handleScrollToExpanded(groupName);
            }
        });
    },
    { deep: true }
);

const selectItem = (child) => {
    emit('select-connector', child);
};

const sendInvitation = () => {
    emit('send-invitation', {
        from_email: frontegg.authState.user?.email,
        tenantId: frontegg.authState.user?.tenantId,
        to_email: colleagueEmail.value,
        message: invitationMessage.value,
        connectorType: props.type.toLowerCase(),
        returnUrl: window.location.href
    });
};
</script>
